import { AiAgentBundleId, AiHomeDispatchMode } from './ai-home';
import { ApdmStepAnswer } from './apdm/build-plan';
import { BiAnswer } from './bi/answer';
export enum AiSessionType {
  WorkshopAssistant = 'workshop_assistant',
  CustomerAssistant = 'customer_assistant',
}

export type AISession = {
  id: string;
  sessionType: string;
  /**
   * What this thread is about, decided server-side from the page the
   * client asked with — one task, one lead, or empty for the shop-wide
   * thread. Two screens about the same car share a thread; two different
   * cars never do.
   */
  subjectKey: string;
  createdAt: string;
  updatedAt: string;
};

/** What `GET /sessions/:id/messages` actually returns. */
export type AIMessagePage = {
  messages: AIMessageInfo[];
  hasMore: boolean;
};

export type AIMessageInfo = {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'tool';
  contentType: 'text' | 'markdown' | 'json';
  content: string;
  metadata?: Record<string, any> | null;
  createdAt: string;
};

/** What this build can show; mirrors backend `AiClientFeature`. */
export enum AiClientFeature {
  CoachingSetup = 'coaching_setup',
  /** The BI widgets; without it the server answers numbers as text. */
  Bi = 'bi',
  /** The campaign planner's card for uploading a customer sheet. */
  ApdmUploadCard = 'apdm_upload_card',
}

/** What this build can show: the chat declares it, and so does the home. */
export const kAiClientFeatures: AiClientFeature[] = [
  AiClientFeature.CoachingSetup,
  AiClientFeature.Bi,
  AiClientFeature.ApdmUploadCard,
];

export type AIAskInput = {
  bundleId?: AiAgentBundleId;
  homeDispatch?: AiHomeDispatchMode;
  attachments?: Array<{ attachmentId: string }>;
  message: string;
  sessionType: string;
  pageContext?: AIPageContext;
  /**
   * The conversation this turn continues, from `sessions/for-page`.
   * Sending it is what keeps the transcript on screen and the server's
   * thread in step; omitting it lands the turn in the single legacy
   * session, which is what un-updatable builds still do.
   */
  sessionId?: string;
  /** A card tapped in this conversation's campaign build. */
  apdmAnswer?: ApdmStepAnswer;
  /** A tap in a BI widget of this conversation; never with `apdmAnswer`. */
  biAnswer?: BiAnswer;
  clientFeatures?: AiClientFeature[];
  /** A library case picked in the app, opening a coaching conversation. */
  coachingCaseId?: string;
};

/**
 * Mirror of backend `AiPageParamsMap` (packages/models/src/ai/page-context.ts).
 * When adding a new screen, update BOTH this map and the backend's catalog
 * — the ai-gateway rejects (`400`) any `page` not listed below.
 *
 * This catalog also drives `client_action` navigation: every entry must
 * have a real destination wired in the slug→navigate resolver
 * (`src/app/components/ai/navigateForSlug.ts`).
 */
export type AIPageParamsMap = {
  // ── tab / list screens ──
  task_list: undefined;
  history_list: undefined;
  inbox: undefined;
  maintenance_plans: undefined;
  leads_center: undefined;
  reservations_center: undefined;
  profile: undefined;
  settings: undefined;

  // ── task-scoped screens ──
  task_detail: { taskNo: string };
  pre_inspection: { taskNo: string };
  delivery_check: { taskNo: string };
  inspection: { taskNo: string; flowId: string };
  construction_job: { taskNo: string; jobId: number };
  construction_schedule: { taskNo: string };
  order_detail: { taskNo: string };
  pending_issues: { taskNo: string };
  quotation: { taskNo: string };
  settlement: { taskNo: string };
  task_timeline: { taskNo: string };

  // ── service history ──
  service_record: {
    taskNo: string;
    orderNo: string;
    serviceRecordNo?: string;
  };

  // ── report screens ──
  report: { orderNo: string; reportNo: string };
  construction_report: { orderNo: string; reportNo: string };
  delivery_check_report: { orderNo: string; reportNo: string };
  inspection_report: { orderNo: string; reportNo: string };
  pre_inspection_report: { orderNo: string; reportNo: string };

  // ── lead / reservation ──
  lead_detail: { leadId: string };
  reservation_detail: { reservationId: string };

  // ── chat ──
  chat_room: { roomId: string };

  // ── BI ──
  bi_dashboard: { dashboardId: string };
  /** An uploaded dataset id or a built-in model key. */
  bi_dataset: { datasetId: string };
  /** A card's own page: "this chart" is the card. */
  bi_card: { dashboardId: string; cardId: string };
};

export type AIPageSlug = keyof AIPageParamsMap;

export type AIPageContext = {
  [K in AIPageSlug]: AIPageParamsMap[K] extends undefined
    ? { page: K; params?: Record<string, never> }
    : { page: K; params: AIPageParamsMap[K] };
}[AIPageSlug];

// ──────────────────────────────────────────────────────────────────
// client_action — mirror of backend `ClientAction` discriminated union
// (packages/models/src/ai/client-action.ts).
// ──────────────────────────────────────────────────────────────────

export type AIClientAction =
  | AIClientActionNavigate
  | AIClientActionCall
  | AIClientActionCopy
  | AIClientActionShare;

export type AIClientActionNavigate = {
  [K in AIPageSlug]: AIPageParamsMap[K] extends undefined
    ? {
        action: 'navigate';
        page: K;
        params?: Record<string, never>;
        label: string;
        confirm?: boolean;
      }
    : {
        action: 'navigate';
        page: K;
        params: AIPageParamsMap[K];
        label: string;
        confirm?: boolean;
      };
}[AIPageSlug];

export type AIClientActionCall = {
  action: 'call';
  phone: string;
  label: string;
  confirm?: boolean;
};

export type AIClientActionCopy = {
  action: 'copy';
  text: string;
  label: string;
  toast?: string;
  confirm?: boolean;
};

export type AIClientActionShare = AIClientActionShareReport;

export type AIClientActionShareReport = {
  action: 'share';
  shareType: 'report';
  orderNo: string;
  reportNo: string;
  licensePlateNo: string;
  reportType: AIClientActionReportType;
  shareMethod?: AIClientActionShareMethod;
  shareTarget?: AIClientActionShareTarget;
  label: string;
  confirm?: boolean;
};

export type AIClientActionReportType =
  | 'overall'
  | 'pre_inspection'
  | 'inspection'
  | 'construction'
  | 'delivery_check'
  | 'sampling_check';

export type AIClientActionShareMethod = 'wechat' | 'wework' | 'sms';

export type AIClientActionShareTarget = 'customer' | 'sa';

export type AISSETokenEvent = { text: string };
export type AISSEToolCallEvent = { name: string; args: any };
export type AISSEToolResultEvent = { name: string; result: any };
export type AISSEDoneEvent = {
  messageId: string;
  usage: { inputTokens: number; outputTokens: number };
};
export type AISSEErrorEvent = { message: string; retryAfter?: number };

export type AISuggestion = {
  text: string;
  category?: string;
};

export type AIAssistantMessage = {
  id?: string;
  localId: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  toolCalls?: AIToolCallInfo[];
  reaction?: 'up' | 'down' | null;
  createdAt: Date;
};

export type AIToolCallInfo = {
  name: string;
  status: 'pending' | 'completed';
};
